import allUsersService from "../../../../../services/all-users.service";
import { UserDisabledInputsContainer } from "../../../../../pages/user/settings/personal-info/user-inputs/styled";
import BarekamText from "../../../../../pages/components/text";
import { Message, MessageSections } from "../../../../../pages/components/message/message";
import { ShipmentPriceContainer, UserFoundContainer, UserNotFoundContainer } from "../styled";
import { GapContainer } from "../../../../../pages/components/card/styled";
import { Done, InfoOutlined } from "@mui/icons-material";
import ExistingOrderDetails from "./existing-order-details";
import BarekamInput from "../../../../../pages/components/input";
import BarekamButton, { DialogButtons, SimpleButtons } from "../../../../../pages/components/button";
import { Grid, ListItemText } from "@mui/material";
import { VolumeContainer } from "../../../../../pages/calculate/styled";
import { StandardDialogFooter } from "../../../../../pages/components/dialogs/standard-dialog/standard-dialog";
import React, { useState } from "react";
import WarningIcon from "@mui/icons-material/Warning";
import { OrderModel } from "../../../../../pages/shared/models/order/order-item.model";
import { OrderUpdateDataModel } from "../add-product";
import { BarekamIcon, IconVariants } from "../../../../../pages/components/icon/barekam-icon";
import { BarekamSelect } from "../../../../../pages/components/select/select";
import { BarekamMenuItem, SelectedBarekamMenuItem } from "../../../../../pages/components/select/styled/orders";
import { useAppSelector } from "../../../../../redux/store";
import { AddProductDialogContent } from "../../styled";
import { getMessage } from "../../../../../pages/shared/helper-functions";

const SecondStep = ({
    trackingNumber,
    order,
    closePopup,
    onProductSave,
    errors,
    onChange,
    updateData,
    volume,
    prevStep,
    isLoading
}: SecondStepProps) => {
    const { messageCache, languageId } = useAppSelector(state => state.messagesReducer);

    const [checkedForRecipient, setCheckedForRecipient] = useState(false);
    const [recipientFound, setRecipientFound] = useState(false);
    const { productStores } = useAppSelector(state => state.staticDataReducer);
    const { lists } = useAppSelector(state => state.CRMReducer);
    const { currentUser } = useAppSelector(state => state.currentUserReducer);

    const [filteredStores] = useState(productStores.filter(store => store.isOther || store.country?.countryId === currentUser?.locationId.countryId))

    function checkAddress2() {
        if (updateData.addressLine2) {
            allUsersService.checkUserForExistence({ addressLine2ForLand: updateData.addressLine2 })
                .then(response => {
                    setCheckedForRecipient(true);
                    setRecipientFound(!!response)
                })
                .catch(reason => {
                    setCheckedForRecipient(true);
                    setRecipientFound(false)
                })
        }
    }

    const UserNotFound = () => (
        <UserNotFoundContainer>
            <WarningIcon />

            <BarekamText variant={'m'} bold>
                <Message messageKey={checkedForRecipient ? 'no_matching_results' : 'no_recipient'}
                    sectionId={MessageSections.ORDERS} />
            </BarekamText>
        </UserNotFoundContainer>
    );

    const UserFound = () => (
        <UserFoundContainer>
            <Done />

            <BarekamText variant={'m'} bold>
                <Message messageKey={'user_found'} sectionId={MessageSections.ORDERS} />
            </BarekamText>
        </UserFoundContainer>
    );

    return (
        <>
            <AddProductDialogContent>
                <UserDisabledInputsContainer>
                    <BarekamButton type={SimpleButtons.LINK} onClick={() => prevStep()}>
                        <BarekamIcon variant={IconVariants.back} />
                    </BarekamButton>

                    <div>
                        <BarekamText variant={"s"} opaque>
                            <Message messageKey={"tracking_number"} sectionId={MessageSections.ORDERS} />
                        </BarekamText>

                        <BarekamText variant={"m"} fontWeight={500}>
                            {trackingNumber}
                        </BarekamText>
                    </div>
                </UserDisabledInputsContainer>

                <ShipmentPriceContainer>
                    <BarekamText variant={'m'} opaque>
                        <Message messageKey={'shipment_price'} sectionId={MessageSections.ORDERS} />
                    </BarekamText>

                    <BarekamText variant={'m'} bold>
                        {updateData?.shippingPrice
                            ? <GapContainer>
                                {updateData?.shippingPrice} <Message messageKey={'amd'}
                                    sectionId={MessageSections.MAIN} />
                            </GapContainer>
                            : '-'
                        }

                        {volume
                            ? <GapContainer>
                                <InfoOutlined opacity={0.6} />

                                <BarekamText variant={'m'} opaque>
                                    <Message messageKey={'volumetric_weight_info'} sectionId={MessageSections.ORDERS} />
                                </BarekamText>
                            </GapContainer>
                            : ''
                        }
                    </BarekamText>
                </ShipmentPriceContainer>

                {order && order.products
                    ? <ExistingOrderDetails order={order} languageId={languageId} />
                    : ''
                }

                {!order && !recipientFound
                    ? <>
                        <UserNotFound />

                        <GapContainer style={{ gap: '1rem' }}>
                            <BarekamInput key={'address2'}
                                customInputVariant={"standard"}
                                placeholder={getMessage('address2', MessageSections.ORDERS, messageCache, languageId)}
                                value={updateData.addressLine2}
                                onChange={(event) => onChange('addressLine2', event.target.value)}
                                error={!!errors['address2']}
                                errorMessage={errors['address2'] && getMessage(errors['address2'], MessageSections.ORDERS, messageCache, languageId)}
                                width={'50%'} />

                            <BarekamButton type={DialogButtons.SAVE}
                                onClick={() => checkAddress2()}>
                                <BarekamText variant={'s'} fontWeight={700}>
                                    <Message messageKey={'check'} sectionId={MessageSections.ORDERS} />
                                </BarekamText>
                            </BarekamButton>
                        </GapContainer>
                    </>
                    : <UserFound />
                }

                <BarekamInput key={'weight'}
                    customInputVariant={"standard"}
                    placeholder={getMessage('weight', MessageSections.ORDERS, messageCache, languageId)}
                    value={updateData.weight || ''}
                    onChange={(event) => onChange('weight', event.target.value)}
                    type={'string'}
                    error={!!errors['weight']}
                    errorMessage={errors['weight'] && getMessage(errors['weight'], MessageSections.ORDERS, messageCache, languageId)}
                    width={'50%'}
                    required
                    validNumber
                    />

                <Grid container columnSpacing={'0.25rem'} rowSpacing={'1rem'}>
                    {!order && (
                        <>
                            <Grid item xs={6}>
                                <BarekamSelect
                                    variant={'outlined'}
                                    label={<BarekamText variant={'1rem'}>
                                        <Message messageKey={'product_store'} sectionId={MessageSections.ORDERS} />
                                    </BarekamText>}
                                    value={updateData?.product[0]?.store && updateData?.product[0]?.store?.storeId || ''}
                                    type={"standard"}
                                    error={!!errors['store']}
                                    errorMessage={errors['store'] && getMessage(errors['store'], MessageSections.ORDERS, messageCache, languageId)}
                                    fullWidth
                                    required>
                                    {
                                        filteredStores.map(store => {
                                            if (updateData?.product[0]?.store && (updateData?.product[0]?.store?.storeId === store.storeId)) {
                                                return (
                                                    <SelectedBarekamMenuItem key={store.storeId}
                                                        value={store.storeId}
                                                        onClick={() => onChange('store', store)}>
                                                        <ListItemText style={{ margin: 'unset' }}
                                                            primary={store.name && store.name[languageId]}
                                                        />
                                                    </SelectedBarekamMenuItem>
                                                )
                                            } else {
                                                return (
                                                    <BarekamMenuItem key={store.storeId}
                                                        value={store.storeId}
                                                        onClick={() => onChange('store', store)}>
                                                        <ListItemText style={{ margin: 'unset' }}
                                                            primary={store.name && store.name[languageId]}
                                                        />
                                                    </BarekamMenuItem>
                                                );
                                            }
                                        })
                                    }
                                </BarekamSelect>
                            </Grid>

                            {updateData?.product[0]?.store?.isOther && (
                                <Grid item xs={6}>
                                    <BarekamInput value={updateData?.product[0]?.storeName}
                                        placeholder={getMessage('store_name', MessageSections.ORDERS, messageCache, languageId)}
                                        customInputVariant={"standard"}
                                        width={'100%'}
                                        error={!!errors['storeName']}
                                        errorMessage={errors['storeName'] && getMessage(errors['storeName'], MessageSections.ORDERS, messageCache, languageId)}
                                        onChange={(event) => onChange('storeName', event.target.value)}
                                        required />
                                </Grid>
                            )}
                        </>
                    )}

                    <Grid item xs={6}>
                        <BarekamSelect
                            variant={'outlined'}
                            label={<BarekamText variant={'1rem'}>
                                <Message messageKey={'crm_list'} sectionId={MessageSections.ORDERS} />
                            </BarekamText>}
                            value={updateData?.crmListId || ''}
                            type={"standard"}
                            error={!!errors['crmListId']}
                            errorMessage={errors['crmListId'] && getMessage(errors['crmListId'], MessageSections.ORDERS, messageCache, languageId)}
                            fullWidth
                            required>
                            {
                                lists.map(list => {
                                    return (
                                        <BarekamMenuItem key={list.id}
                                            value={list.id}
                                            onClick={() => onChange('crmListId', list.id)}>
                                            {list.name}
                                        </BarekamMenuItem>
                                    );
                                })
                            }
                        </BarekamSelect>
                    </Grid>

                    <Grid item xs={12}>
                        <BarekamText variant={'m'}>
                            <Message messageKey={'dimensions'} sectionId={MessageSections.ORDERS} />:
                        </BarekamText>
                    </Grid>

                    {volume
                        ? <Grid item xs={12}>
                            <VolumeContainer>
                                <BarekamText variant={'m'} opaque>
                                    <Message messageKey={'volume'} sectionId={MessageSections.ORDERS} />:
                                </BarekamText>

                                <BarekamText variant={'m'}>
                                    {volume}
                                </BarekamText>

                                <BarekamText variant={'m'}>
                                    <Message messageKey={'cm_3'} sectionId={MessageSections.ORDERS} />
                                </BarekamText>
                            </VolumeContainer>
                        </Grid>
                        : ''
                    }

                    <Grid item xs={4}>
                        <BarekamInput value={updateData.width || ''}
                            placeholder={getMessage('width_cm', MessageSections.ORDERS, messageCache, languageId)}
                            label={getMessage('width_cm', MessageSections.ORDERS, messageCache, languageId)}
                            type={"number"}
                            customInputVariant={"standard"}
                            width={'100%'}
                            error={!!errors['width']}
                            errorMessage={errors['width'] && getMessage(errors['width'], MessageSections.ORDERS, messageCache, languageId)}
                            onChange={(event) => onChange('width', +event.target.value)} />
                    </Grid>

                    <Grid item xs={4}>
                        <BarekamInput value={updateData.height || ''}
                            type={"number"}
                            placeholder={getMessage('height_cm', MessageSections.ORDERS, messageCache, languageId)}
                            label={getMessage('height_cm', MessageSections.ORDERS, messageCache, languageId)}
                            customInputVariant={"standard"}
                            width={'100%'}
                            error={!!errors['height']}
                            errorMessage={errors['height'] && getMessage(errors['height'], MessageSections.ORDERS, messageCache, languageId)}
                            onChange={(event) => onChange('height', +event.target.value)} />
                    </Grid>

                    <Grid item xs={4}>
                        <BarekamInput value={updateData.length || ''}
                            placeholder={getMessage('length_cm', MessageSections.ORDERS, messageCache, languageId)}
                            label={getMessage('length_cm', MessageSections.ORDERS, messageCache, languageId)}
                            type={"number"}
                            customInputVariant={"standard"}
                            width={'100%'}
                            error={!!errors['length']}
                            errorMessage={errors['length'] && getMessage(errors['length'], MessageSections.ORDERS, messageCache, languageId)}
                            onChange={(event) => onChange('length', +event.target.value)} />
                    </Grid>

                    <Grid item xs={12}>
                        <BarekamInput value={updateData.notes}
                            placeholder={getMessage('notes', MessageSections.ORDERS, messageCache, languageId)}
                            isReadonly
                            label={getMessage('notes', MessageSections.ORDERS, messageCache, languageId)}
                            customInputVariant={"standard"}
                            width={'100%'}
                            error={!!errors['notes']}
                            errorMessage={errors['notes'] && getMessage(errors['notes'], MessageSections.ORDERS, messageCache, languageId)}
                            onChange={(event) => onChange('notes', event.target.value)} />
                    </Grid>

                    <Grid item xs={12}>
                        <BarekamInput value={updateData.comment}
                            placeholder={getMessage('comment', MessageSections.ORDERS, messageCache, languageId)}
                            label={getMessage('comment', MessageSections.ORDERS, messageCache, languageId)}
                            customInputVariant={"standard"}
                            width={'100%'}
                            error={!!errors['comment']}
                            errorMessage={errors['comment'] && getMessage(errors['comment'], MessageSections.ORDERS, messageCache, languageId)}
                            onChange={(event) => onChange('comment', event.target.value)} />
                    </Grid>
                </Grid>
            </AddProductDialogContent>

            <StandardDialogFooter saveLabel={<Message messageKey={'save'} sectionId={MessageSections.ORDERS} />}
                onSave={() => !isLoading && onProductSave()}
                onCancel={() => closePopup()} />
        </>
    )
}

interface SecondStepProps {
    trackingNumber: string,
    volume: number | undefined,
    updateData: OrderUpdateDataModel,
    isLoading: boolean,
    errors: { [key: string]: string },

    onChange: (field: string, value: any) => void,
    nextStep: () => void;
    prevStep: () => void;
    closePopup: () => void;
    onProductSave: () => void;
    order?: OrderModel,
}

export default SecondStep;